export interface CookieAttributes {
  [property: string]: unknown;
  domain?: string;
  expires?: Date;
  maxAgeInDays?: number;
  path?: string;
  sameSite?: 'Strict' | 'Lax' | 'None';
  secure?: boolean;
}

// Chrome now imposes a 400-day limit on cookies lifetime https://developer.chrome.com/blog/cookie-max-age-expires/
export const MAX_COOKIE_AGE_IN_DAYS = 400;

const SECONDS_PER_DAY = 86400;

const capitalize = (str: string): string => str[0].toUpperCase().concat(str.slice(1));

/**
 * Gets the cookie (value) with the given name.
 */
export const getCookie = (name: string): string | undefined => {

  if (!document.cookie) {
    return undefined;
  }

  return document.cookie.split(';')
    .map((x) => x.trim())
    .find((x) => x.startsWith(name))
    ?.split('=')[1]
    ?.trim();
};

/**
 * Sets the cookie with the given name to the given value and with the given optional attributes.
 */
export const setCookie = (name: string, value: string, attributes: CookieAttributes = {}): void => {

  // Convert maxAgeInDays to max-age (in seconds), expires to GMT date string, pass other attributes through unchanged
  const { maxAgeInDays, expires, ...otherAttributes } = attributes;
  const maxAgeInSeconds = maxAgeInDays ? maxAgeInDays * SECONDS_PER_DAY : undefined;

  const cookieAttributes: Record<string, unknown> =
    { ...otherAttributes, Expires: expires?.toUTCString(), 'Max-Age': maxAgeInSeconds };

  // Filter out undefined/falsy attributes and convert to semicolon-delimited string
  // Names are capitalized and boolean entries are written without a value.
  const attributesString = Object.keys(cookieAttributes)
    .filter((k) => !!cookieAttributes[k])
    .map((k) => typeof cookieAttributes[k] === 'boolean'
      ? `; ${capitalize(k)}`
      : `; ${capitalize(k)}=${cookieAttributes[k]}`)
    .join('');

  document.cookie = `${name}=${value}${attributesString}`;
};
